import React, { ReactNode } from 'react';
import { EnumCellProps, EnumOption, WithClassname } from '@jsonforms/core';
import { Select } from '@mantine/core';
import { ControlProps } from '../../types/controlProps';
import { useVisibilityLevelContext } from '../../contexts/VisibilityLevelContext';
import { MantineInputWrapper } from '../complex';
import { getDefaultValue, useSelectedObject } from '../../hooks/useSelectedObject';
import { useIsParentSelected } from '../../hooks/useIsParentSelected';

export interface WithOptionLabel {
  getOptionLabel?(option: EnumOption): string;
  renderOption?(option: EnumOption): ReactNode;
  filterOptions?(options: EnumOption[], inputValue: string): EnumOption[];
}

export const MantineAutocomplete = (props: ControlProps & EnumCellProps & WithClassname & WithOptionLabel) => {
  const {
    description,
    errors,
    visible,
    required,
    label,
    data,
    className,
    id,
    enabled,
    path,
    handleChange,
    options,
    getOptionLabel,
    filterOptions,
  } = props;

  const { isLevelEnabled } = useVisibilityLevelContext();
  const [inputValue, setInputValue] = React.useState(data ?? '');

  const propertyLevel = props.schema.level;
  const findOption = options?.find((o) => o.value === data) ?? null;

  const { isParentSelected } = useIsParentSelected(path);
  const disabled = !isParentSelected;
  const fallbackValue = disabled ? '' : getDefaultValue(props.path);

  if (!visible || !isLevelEnabled(propertyLevel)) {
    return null;
  }

  const filteredOptions = filterOptions && options ? filterOptions(options, inputValue) : options;

  return (
    <MantineInputWrapper required={required} label={label} description={description} error={errors}>
      <fieldset disabled={disabled}>
        <Select
          miw={200}
          maw={320}
          className={className}
          id={id}
          disabled={!enabled}
          value={findOption ? findOption.value : fallbackValue}
          onChange={(newValue) => {
            handleChange(path, newValue);
          }}
          data={filteredOptions?.map((option) => ({
            value: option.value,
            label: getOptionLabel ? getOptionLabel(option) : option.label,
          }))}
          searchable
          placeholder={!filteredOptions?.length ? 'No options' : undefined}
          onSearchChange={(newInputValue) => setInputValue(newInputValue)}
        />
      </fieldset>
    </MantineInputWrapper>
  );
};
